import { actionToId } from '../core/actions';
import type { Bid } from '../types';
import type { ValidAction } from '../../multiplayer/types';
import type { ActionEvaluation, BidEvaluation } from './monte-carlo';
import {
  AI_BID_RECEIPT_VERSION,
  AI_DECLARATION_RECEIPT_VERSION,
  AI_PLAY_RECEIPT_VERSION,
  type AIActionChoiceMechanism,
  type AIPlayDecisionEvidence,
  type AIBidDecisionEvidence,
  type AIPlayCandidateEvidence,
  type AIPlayCandidateComparison,
  type AIPlayComparisonOutcome,
  type AIPlaySearchStopReason,
  type ProtectedCountDecision,
  type AIDeclarationDecisionEvidence,
} from './types';
import type { DeclarationDecision } from './hand-strength';
import {
  assessContextualCountControl,
  compareContextualControl,
  compareContextualControlDetailed,
  futureControlDescription,
} from './contextual-assessment';
import type { AIPairedPlayConfidence } from './types';

function unscoredCandidate(action: ValidAction): AIPlayCandidateEvidence {
  return { actionId: actionToId(action.action) };
}

function scoredCandidate(
  evaluation: ActionEvaluation,
  comparison: AIPlayCandidateComparison,
): AIPlayCandidateEvidence {
  return {
    actionId: actionToId(evaluation.action.action),
    avgTeamPoints: evaluation.avgTeamPoints,
    winRate: evaluation.winRate,
    contractUtility: evaluation.contractUtility,
    simulationCount: evaluation.simulationCount,
    partnerSafety: evaluation.partnerSafety,
    partnerWinRate: evaluation.partnerWinRate,
    overtakesPartner: evaluation.overtakesPartner,
    candidateWinsTrick: evaluation.candidateWinsTrick,
    countDestination: evaluation.countDestination,
    countRisk: evaluation.countRisk,
    liveStanding: evaluation.liveStanding,
    bossPreservation: evaluation.bossPreservation,
    likelyFutureLeadValue: evaluation.likelyFutureLeadValue,
    protectedCountValue: evaluation.protectedCountValue,
    promotionSupport: evaluation.promotionSupport,
    leadAccess: evaluation.leadAccess,
    remainingControl: evaluation.remainingControl,
    cashability: evaluation.cashability,
    contractSecuringRate: evaluation.contractSecuringRate,
    sacrificedProtectedCountValue: evaluation.sacrificedProtectedCountValue,
    ...(evaluation.retainedTrumpControl !== undefined
      ? { retainedTrumpControl: evaluation.retainedTrumpControl }
      : {}),
    ...(evaluation.protectsPartnerTrumpControl !== undefined
      ? { protectsPartnerTrumpControl: evaluation.protectsPartnerTrumpControl }
      : {}),
    ...(evaluation.leadPressure !== undefined ? { leadPressure: evaluation.leadPressure } : {}),
    ...(evaluation.reentryPlanValue !== undefined
      ? {
          actingSeatRetainedTrump: evaluation.actingSeatRetainedTrump,
          externalVoidRuffOpportunity: evaluation.externalVoidRuffOpportunity,
          retainedUsefulContinuation: evaluation.retainedUsefulContinuation,
          weakOffCleanup: evaluation.weakOffCleanup,
          reentryPlanValue: evaluation.reentryPlanValue,
        }
      : {}),
    comparison,
  };
}

type ProtectedCountDecisionCandidate = Pick<
  AIPlayCandidateEvidence,
  | 'actionId'
  | 'protectedCountValue'
  | 'leadAccess'
  | 'cashability'
  | 'sacrificedProtectedCountValue'
  | 'partnerSafety'
  | 'overtakesPartner'
  | 'countDestination'
  | 'countRisk'
  | 'contractSecuringRate'
  | 'reentryPlanValue'
  | 'actingSeatRetainedTrump'
  | 'externalVoidRuffOpportunity'
  | 'retainedUsefulContinuation'
  | 'weakOffCleanup'
  | 'comparison'
>;

/** True when one comparison proves a strict secure-contract advantage. */
export function isSecureContractContributionComparison(
  selected: ProtectedCountDecisionCandidate,
  alternative: ProtectedCountDecisionCandidate,
): boolean {
  if (selected.partnerSafety !== 'secure' || alternative.partnerSafety !== 'secure') {
    return false;
  }
  const selectedIsDonation =
    (selected.countRisk ?? 0) > 0 && selected.countDestination === 'partner';
  const alternativeIsDonation =
    (alternative.countRisk ?? 0) > 0 && alternative.countDestination === 'partner';
  return (
    (selectedIsDonation || alternativeIsDonation) &&
    (selected.contractSecuringRate ?? 0) > (alternative.contractSecuringRate ?? 0)
  );
}

/** Derive the protected-count receipt reason from public candidate aggregates. */
export function classifyProtectedCountDecision(
  candidates: readonly ProtectedCountDecisionCandidate[],
  selectedActionId: string,
  pairedConfidence?: Pick<AIPairedPlayConfidence, 'clearLeader' | 'leaderActionId' | 'unresolvedActionIds'>,
): ProtectedCountDecision {
  const candidate = candidates.find(({ actionId }) => actionId === selectedActionId);
  if (candidate === undefined) return 'none';
  const decisiveAlternative = candidates
    .filter((alternative) =>
      alternative.actionId !== selectedActionId &&
      alternative.comparison !== undefined &&
      alternative.comparison.outcome !== 'stable-order' &&
      alternative.comparison.outcome !== 'contract-utility',
    )
    .sort((left, right) => left.comparison!.order - right.comparison!.order)[0];
  const candidateCountControl = assessContextualCountControl(candidate);
  const decisiveCountControl = decisiveAlternative === undefined
    ? undefined
    : assessContextualCountControl(decisiveAlternative);
  const sacrificedValue = candidate.sacrificedProtectedCountValue ?? 0;
  if (
    sacrificedValue > 0 &&
    pairedConfidence?.clearLeader === true &&
    pairedConfidence.leaderActionId === selectedActionId
  ) {
    return 'clear-contract-sacrifice';
  }
  if (
    pairedConfidence?.clearLeader === false &&
    decisiveAlternative?.comparison?.outcome === 'protected-count' &&
    decisiveCountControl !== undefined &&
    candidateCountControl.cashableRetainedCount > decisiveCountControl.cashableRetainedCount &&
    candidateCountControl.value > decisiveCountControl.value
  ) {
    return 'kept-unresolved-count';
  }
  if (
    sacrificedValue > 0 &&
    pairedConfidence?.clearLeader === false &&
    candidate.partnerSafety === 'secure' &&
    candidate.countDestination === 'partner' &&
    decisiveAlternative?.comparison?.outcome === 'secure-contract-contribution' &&
    isSecureContractContributionComparison(candidate, decisiveAlternative)
  ) {
    return 'secure-partner-contribution';
  }
  if (
    sacrificedValue > 0 &&
    pairedConfidence?.clearLeader === false &&
    candidateCountControl.safePartnerCount > 0 &&
    candidates.some((alternative) => {
      if (
        alternative.actionId === selectedActionId ||
        !pairedConfidence.unresolvedActionIds.includes(alternative.actionId)
      ) return false;
      const alternativeCountControl = assessContextualCountControl(alternative);
      return candidateCountControl.value >= alternativeCountControl.value &&
        candidateCountControl.value - candidateCountControl.safePartnerCount < alternativeCountControl.value;
    })
  ) {
    return 'first-safe-count-contribution';
  }
  if (
    pairedConfidence?.clearLeader === false &&
    (candidate.reentryPlanValue ?? 0) > 0 &&
    decisiveAlternative?.comparison?.outcome === 'trump-reentry'
  ) {
    return 'retained-trump-reentry';
  }
  if (
    sacrificedValue > 0 &&
    pairedConfidence?.clearLeader === false &&
    candidates.some((alternative) =>
      alternative.actionId !== selectedActionId &&
      (alternative.protectedCountValue ?? 0) >= sacrificedValue &&
      alternative.cashability === 0)
  ) {
    return 'dead-control-release';
  }
  return 'none';
}

function compareStableIds(left: string, right: string): number {
  if (left < right) return -1;
  if (left > right) return 1;
  return 0;
}

function buildComparisons(
  evaluations: readonly ActionEvaluation[],
  selected: ValidAction,
  tolerance: number,
  selectionActionIds?: readonly string[],
): Map<string, AIPlayCandidateComparison> {
  const sorted = evaluations
    .map((evaluation, index) => ({
      evaluation,
      index,
      actionId: actionToId(evaluation.action.action),
    }))
    .sort(
      (left, right) =>
        right.evaluation.contractUtility - left.evaluation.contractUtility ||
        compareStableIds(left.actionId, right.actionId) ||
        left.index - right.index,
    );
  const selectionActionIdSet = selectionActionIds === undefined
    ? undefined
    : new Set(selectionActionIds);
  const bestScore = sorted[0]?.evaluation.contractUtility ?? 0;
  const nearEqual = selectionActionIdSet === undefined
    ? sorted.filter(({ evaluation }) => bestScore - evaluation.contractUtility <= tolerance)
    : sorted.filter(({ actionId }) => selectionActionIdSet.has(actionId));

  // The same contextual comparator controls root selection, rollout order, and receipts.
  const afterControl = nearEqual.reduce<typeof nearEqual>((best, candidate) => {
    if (best.length === 0) return [candidate];
    const comparison = compareContextualControl(candidate.evaluation, best[0]!.evaluation);
    if (comparison > 0) return [candidate];
    if (comparison === 0) return [...best, candidate];
    return best;
  }, []);
  const afterControlSet = new Set(afterControl.map(({ actionId }) => actionId));
  const nearEqualIds = new Set(nearEqual.map(({ actionId }) => actionId));
  const comparisonOrder = [
    ...afterControl,
    ...nearEqual.filter(({ actionId }) => !afterControlSet.has(actionId)),
    ...sorted.filter(({ actionId }) => !nearEqualIds.has(actionId)),
  ];
  const selectedId = actionToId(selected.action);
  const selectedEvaluation = evaluations.find(
    (evaluation) => actionToId(evaluation.action.action) === selectedId,
  );
  const result = new Map<string, AIPlayCandidateComparison>();

  comparisonOrder.forEach(({ evaluation, actionId }, order) => {
    let outcome: AIPlayComparisonOutcome = 'stable-order';
    if (actionId === selectedId) {
      outcome = 'selected';
    } else if (!nearEqualIds.has(actionId)) {
      outcome = 'contract-utility';
    } else if (selectedEvaluation) {
      const controlComparison = compareContextualControlDetailed(selectedEvaluation, evaluation);
      if (controlComparison.comparison > 0 && controlComparison.outcome !== 'equal') {
        outcome = controlComparison.outcome;
      }
    }
    result.set(actionId, { outcome, order });
  });

  return result;
}

export function buildUnscoredPlayEvidence(
  candidates: readonly ValidAction[],
  selected: ValidAction,
  choiceMechanism: Extract<AIActionChoiceMechanism, 'forced' | 'fallback'>,
): AIPlayDecisionEvidence {
  return {
    version: AI_PLAY_RECEIPT_VERSION,
    phase: 'playing',
    objective: 'unscored',
    candidates: candidates.map(unscoredCandidate),
    selectedActionId: actionToId(selected.action),
    choiceMechanism,
  };
}

export function buildScoredPlayEvidence(
  evaluations: readonly ActionEvaluation[],
  selected: ValidAction,
  options: {
    choiceMechanism: Extract<
      AIActionChoiceMechanism,
      'highest-score' | 'near-equal-sample' | 'contextual-preference' | 'contextual-sample'
    >;
    tolerance: number;
    candidateCount: number;
    preferredCandidateCount: number;
    pairedConfidence?: AIPairedPlayConfidence;
    searchStopReason?: AIPlaySearchStopReason;
    selectionActionIds?: readonly string[];
  },
): AIPlayDecisionEvidence {
  const comparisons = buildComparisons(
    evaluations,
    selected,
    options.tolerance,
    options.selectionActionIds,
  );
  const selectedEvaluation = evaluations.find(
    (evaluation) => actionToId(evaluation.action.action) === actionToId(selected.action),
  );
  const futureControlEliminationCount = Array.from(comparisons.values()).filter(
    (c) => c.outcome === 'future-control' || c.outcome === 'count-risk',
  ).length;
  const futureControlExplanation =
    futureControlEliminationCount > 0 && selectedEvaluation
      ? `lost less future control than ${futureControlEliminationCount} near-equal alternative${futureControlEliminationCount === 1 ? '' : 's'}: ${futureControlDescription(selectedEvaluation)}`
      : undefined;
  const pairedConfidence = options.pairedConfidence;
  if (evaluations.length > 1 && pairedConfidence === undefined) {
    throw new Error('Scored play evidence requires final paired confidence.');
  }
  if (evaluations.length > 1 && options.searchStopReason === undefined) {
    throw new Error('Scored play evidence requires a search stop reason.');
  }

  const selectedActionId = actionToId(selected.action);
  const candidateEvidence = evaluations.map((evaluation) =>
    scoredCandidate(
      evaluation,
      comparisons.get(actionToId(evaluation.action.action)) ?? {
        outcome: 'stable-order',
        order: evaluations.indexOf(evaluation),
      },
    ),
  );
  const selectedProtectedCountDecision = classifyProtectedCountDecision(
    candidateEvidence,
    selectedActionId,
    pairedConfidence,
  );
  const protectedCountExplanation = selectedEvaluation === undefined
    ? undefined
    : describeProtectedCountDecision(selectedEvaluation, selectedProtectedCountDecision);

  return {
    version: AI_PLAY_RECEIPT_VERSION,
    phase: 'playing',
    objective: 'contract-utility',
    candidates: candidateEvidence,
    selectedActionId: actionToId(selected.action),
    choiceMechanism: options.choiceMechanism,
    ...(pairedConfidence ? { pairedConfidence } : {}),
    ...(options.searchStopReason ? { searchStopReason: options.searchStopReason } : {}),
    ...(options.choiceMechanism !== 'highest-score'
      ? {
          nearEqual: {
            tolerance: options.tolerance,
            candidateCount: options.candidateCount,
            preferredCandidateCount: options.preferredCandidateCount,
          },
        }
      : {}),
    ...(futureControlExplanation !== undefined
      ? { futureControlExplanation }
      : {}),
    protectedCountDecision: selectedProtectedCountDecision,
    ...(protectedCountExplanation !== undefined
      ? { protectedCountExplanation }
      : {}),
  };
}

function describeProtectedCountDecision(
  evaluation: ActionEvaluation,
  decision: ProtectedCountDecision,
): string | undefined {
  switch (decision) {
    case 'none':
      return undefined;
    case 'kept-unresolved-count':
      return `kept ${evaluation.protectedCountValue} protected count with ${Math.round(evaluation.cashability * 100)}% sampled cashability`;
    case 'secure-partner-contribution':
      return `released ${evaluation.sacrificedProtectedCountValue} protected count to a secure partner trick that immediately made or set the contract`;
    case 'first-safe-count-contribution':
      return 'gave count at the first secure partner opportunity';
    case 'retained-trump-reentry':
      return 'kept one trump for a useful re-entry';
    case 'clear-contract-sacrifice':
      return `released ${evaluation.sacrificedProtectedCountValue} protected count for a clear contract path`;
    case 'dead-control-release':
      return 'released protected count with no reachable cashing route';
  }
}

export function buildBidEvidence(
  evaluations: readonly BidEvaluation[],
  selected: ValidAction,
  nearEqualActionIds: readonly string[],
  objective: AIBidDecisionEvidence['objective'],
  bids: readonly Bid[],
): AIBidDecisionEvidence {
  const selectedId = actionToId(selected.action);
  if (selected.action.type !== 'bid' && selected.action.type !== 'pass') {
    throw new Error('buildBidEvidence: expected bid or pass action');
  }
  const selectedBidAction = selected.action.type === 'bid' ? selected.action : undefined;
  const currentPointHigh = Math.max(0, ...bids.flatMap((bid) =>
    bid.type === 'points' && bid.value !== undefined ? [bid.value] : [],
  ));
  const selectedBidMargin = selectedBidAction?.bid === 'points'
    ? (selectedBidAction.value ?? 0) - currentPointHigh
    : undefined;
  const scoreFor = (evaluation: BidEvaluation): number =>
    objective === 'match-value' ? evaluation.estimatedMatchValue : evaluation.makeBidRate;
  const bestScore = objective === 'unscored' || evaluations.length === 0
    ? undefined
    : Math.max(...evaluations.map(scoreFor));
  const candidates: AIBidDecisionEvidence['candidates'] = evaluations.map((evaluation) => {
    const action = evaluation.action.action;
    if (action.type !== 'bid') throw new Error('buildBidEvidence: expected bid action');
    return {
      actionId: actionToId(action),
      bidType: action.bid,
      ...(action.value !== undefined ? { bidValue: action.value } : {}),
      makeBidRate: evaluation.makeBidRate,
      estimatedMatchValue: evaluation.estimatedMatchValue,
      simulationCount: evaluation.simulationCount,
      ...(bestScore !== undefined ? { scoreGapFromBest: bestScore - scoreFor(evaluation) } : {}),
      ...(action.bid === 'points' && action.value !== undefined
        ? { pointRaiseMargin: action.value - currentPointHigh }
      : {}),
    };
  });
  if (selectedBidAction && !candidates.some((candidate) => candidate.actionId === selectedId)) {
    candidates.push({
      actionId: selectedId,
      bidType: selectedBidAction.bid,
      ...(selectedBidAction.value !== undefined ? { bidValue: selectedBidAction.value } : {}),
      ...(selectedBidMargin !== undefined ? { pointRaiseMargin: selectedBidMargin } : {}),
    });
  }
  if (selected.action.type === 'pass') {
    candidates.push({ actionId: selectedId, bidType: 'pass' });
  }
  return {
    version: AI_BID_RECEIPT_VERSION,
    phase: 'bidding',
    objective,
    candidates,
    selectedActionId: selectedId,
    ...(selectedBidMargin !== undefined ? { selectedBidMargin } : {}),
    nearEqualActionIds: [...nearEqualActionIds],
  };
}

export function buildDeclarationEvidence(
  decision: DeclarationDecision,
  candidates: readonly ValidAction[],
): AIDeclarationDecisionEvidence {
  const rankedCandidates = decision.ranked.map(({ action, evaluation }, order) => ({
    actionId: actionToId(action),
    declaration: evaluation.declaration.type,
    ...(evaluation.declaration.type === 'suit'
      ? { suit: evaluation.declaration.suit }
      : {}),
    order,
    score: evaluation.score,
    risk: evaluation.risk,
    control: evaluation.control,
  }));
  const rankedIds = new Set(rankedCandidates.map(({ actionId }) => actionId));
  const specialCandidates = candidates.flatMap(({ action }) => {
    if (action.type !== 'select-trump' || action.trump.type === 'not-selected') return [];
    if (rankedIds.has(actionToId(action))) return [];
    return [{
      actionId: actionToId(action),
      declaration: action.trump.type,
      ...(action.trump.type === 'suit' && action.trump.suit !== undefined
        ? { suit: action.trump.suit }
        : {}),
    }];
  });
  return {
    version: AI_DECLARATION_RECEIPT_VERSION,
    phase: 'trump_selection',
    objective: 'hand-structure',
    contractTarget: decision.contractTarget,
    candidates: [...rankedCandidates, ...specialCandidates.map((candidate, index) => ({
      ...candidate,
      order: rankedCandidates.length + index,
    }))],
    selectedActionId: actionToId(decision.action),
    choiceReason: decision.reason,
  };
}

export function buildUnscoredDeclarationEvidence(
  candidates: readonly ValidAction[],
  selected: ValidAction,
  contractTarget: number,
): AIDeclarationDecisionEvidence {
  const evidenceCandidates = candidates.flatMap(({ action }) => {
    if (action.type !== 'select-trump' || action.trump.type === 'not-selected') return [];
    return [{
      actionId: actionToId(action),
      declaration: action.trump.type,
      ...(action.trump.type === 'suit' && action.trump.suit !== undefined
        ? { suit: action.trump.suit }
        : {}),
      order: 0,
    }];
  });
  return {
    version: AI_DECLARATION_RECEIPT_VERSION,
    phase: 'trump_selection',
    objective: 'unscored',
    contractTarget,
    candidates: evidenceCandidates.map((candidate, order) => ({ ...candidate, order })),
    selectedActionId: actionToId(selected.action),
    choiceReason: 'fallback',
  };
}
